/**
 * Pre-process LLM markdown before react-markdown + remark-gfm.
 * - Collapses soft line wraps in prose without destroying GFM pipe tables.
 * - Keeps <br> inside pipe-rows as spaces so a row stays one line for the parser.
 */

function isPipeTableSeparator(line: string): boolean {
  const t = line.trim();
  if (!t.includes('|') || !/-/.test(t)) return false;
  if (!/^[\s|:-]+$/.test(t)) return false;
  return /-{2,}/.test(t);
}

/** Line that looks like a GFM table row (header or body), not prose with a stray | */
function isPipeTableRow(line: string): boolean {
  const t = line.trim();
  if (!t.includes('|')) return false;
  if (isPipeTableSeparator(t)) return true;
  const pipes = (t.match(/\|/g) || []).length;
  if (pipes < 2) return false;
  return /^\|/.test(t) || /\|$/.test(t);
}

function looksLikeListStart(line: string): boolean {
  const t = line.trimStart();
  return /^[-*+]\s/.test(t) || /^\d+\.\s/.test(t);
}

/** Previous physical line is an incomplete table row (e.g. multiline cell). */
function tableRowNeedsContinuation(prev: string): boolean {
  const t = prev.trimEnd();
  if (!t.includes('|')) return false;
  if (looksLikeListStart(prev)) return false;
  return !t.endsWith('|');
}

/**
 * Maximal run of table lines: header/separator/rows + continuation lines until a complete row
 * or a blank line or a list/heading start.
 */
function extractTableBlocks(lines: string[], start: number): { end: number } {
  let i = start;
  if (!isPipeTableRow(lines[i])) return { end: start };
  i++;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') break;
    if (looksLikeListStart(line) && i > start) break;
    const prev = lines[i - 1];
    if (isPipeTableRow(line)) {
      i++;
      continue;
    }
    if (tableRowNeedsContinuation(prev)) {
      i++;
      continue;
    }
    break;
  }
  return { end: i };
}

function collapseProseNewlines(chunk: string): string {
  let s = chunk.replace(/\n(?!\n)(?!\s*[-*#]\s)(?!\s*\d+\.\s)(?!\s*\|)/g, ' ');
  s = s.replace(/^(\s*\d+\.)\s*\n(?=\S)/gm, '$1 ');
  return s;
}

/**
 * `<br>` inside a pipe row must become a space; otherwise the row splits and the table breaks.
 * Other `<br>` → newline for paragraph breaks.
 */
function normalizeBrTags(text: string): string {
  return text
    .split('\n')
    .map((line) => {
      if (line.includes('|') && /<br\s*\/?>/i.test(line)) {
        return line.replace(/<br\s*\/?>/gi, ' ');
      }
      return line;
    })
    .join('\n')
    .replace(/<br\s*\/?>/gi, '\n');
}

export function normalizeMarkdownLineBreaks(text: string): string {
  let s = normalizeBrTags(text);
  const lines = s.split('\n');
  const segments: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      segments.push('');
      i++;
      continue;
    }
    const tableEnd = extractTableBlocks(lines, i);
    if (tableEnd.end > i) {
      segments.push(lines.slice(i, tableEnd.end).join('\n'));
      i = tableEnd.end;
    } else {
      const proseStart = i;
      while (i < lines.length && lines[i].trim() !== '') {
        const nextTable = extractTableBlocks(lines, i);
        if (nextTable.end > i) break;
        i++;
      }
      if (i > proseStart) {
        segments.push(collapseProseNewlines(lines.slice(proseStart, i).join('\n')));
      }
    }
  }
  return segments.join('\n');
}
